import { WSServices } from "./WSServices";

// ── DTOs ──────────────────────────────────────────────────────────

export interface QueueEntry {
	roomId: string;
	patientId: string;
	patientName: string;
	reason: string;
	createdAt: string;
	position: number;
}

export interface TranscriptionEntry {
	timestamp: number;
	speaker: "doctor" | "patient";
	text: string;
}

// ── WebRTC Config ─────────────────────────────────────────────────

const ICE_SERVERS: RTCConfiguration = {
	iceServers: [
		{ urls: "stun:stun.l.google.com:19302" },
		{ urls: "stun:stun1.l.google.com:19302" },
	],
};

// ── Service ───────────────────────────────────────────────────────

/**
 * Serviço de teleconsulta — gerencia fila, WebRTC, chat e transcrição.
 *
 * Cada aba gera um `peerId` único para evitar conflitos quando o mesmo
 * usuário abre várias abas (ex: médico + paciente no mesmo browser).
 * O backend usa `sendAll` para eventos de sala, e o frontend filtra
 * por `roomId` e ignora mensagens do próprio `peerId`.
 */
export class TeleconsultationService {
	/** ID único por aba/página — diferencia abas do mesmo usuário */
	private static peerId: string = crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).slice(2);

	private static peerConnection: RTCPeerConnection | null = null;
	private static localStream: MediaStream | null = null;
	private static remoteStream: MediaStream | null = null;
	private static currentRoomId: string | null = null;
	private static recognition: any = null;
	private static transcription: TranscriptionEntry[] = [];
	private static onTranscriptionUpdate: ((entries: TranscriptionEntry[]) => void) | null = null;
	private static _chatCallbacks: Array<(data: { roomId: string; message: string; sender: string; time: string }) => void> = [];

	// ── Fila: Paciente solicita consulta ──────────────────────────

	static requestConsultation(patientName: string, reason: string): string {
		const roomId = crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36);
		WSServices.sendData("teleconsultation:request", { roomId, patientName, reason });
		this.currentRoomId = roomId;
		return roomId;
	}

	static cancelRequest() {
		if (this.currentRoomId) {
			WSServices.sendData("teleconsultation:cancel-request", { roomId: this.currentRoomId });
			this.currentRoomId = null;
		}
	}

	// ── Fila: Médico se registra como disponível ──────────────────

	static registerAsAvailable(doctorName: string) {
		WSServices.sendData("teleconsultation:doctor-available", { doctorName });
	}

	static unregisterAvailable() {
		WSServices.sendData("teleconsultation:doctor-unavailable", {});
	}

	// ── Fila: Médico aceita consulta ──────────────────────────────

	static acceptConsultation(roomId: string, doctorName: string, appointmentId?: string) {
		WSServices.sendData("teleconsultation:doctor-accept", { roomId, doctorName, appointmentId: appointmentId || null });
	}

	// ── Listeners de fila ─────────────────────────────────────────

	static onQueueUpdate(callback: (queue: QueueEntry[]) => void) {
		return WSServices.onReceive<QueueEntry[]>("teleconsultation:queue-update", callback);
	}

	static onConsultationAccepted(callback: (data: { roomId: string; doctorName: string; appointmentId: string | null }) => void) {
		return WSServices.onReceive<{ roomId: string; doctorName: string; appointmentId: string | null }>("teleconsultation:accepted", callback);
	}

	static onNewPatient(callback: (data: QueueEntry) => void) {
		return WSServices.onReceive<QueueEntry>("teleconsultation:new-patient", callback);
	}

	static onPatientLeft(callback: (data: { roomId: string }) => void) {
		return WSServices.onReceive<{ roomId: string }>("teleconsultation:patient-left", callback);
	}

	// ── Media ─────────────────────────────────────────────────────

	static async getLocalMedia(video = true, audio = true): Promise<MediaStream> {
		if (this.localStream) {
			this.localStream.getTracks().forEach((t) => t.stop());
		}
		this.localStream = await navigator.mediaDevices.getUserMedia({ video, audio });
		return this.localStream;
	}

	static getLocalStream(): MediaStream | null {
		return this.localStream;
	}

	static getRemoteStream(): MediaStream | null {
		return this.remoteStream;
	}

	// ── WebRTC Peer Connection ────────────────────────────────────

	static async joinRoom(
		roomId: string,
		onRemoteStream: (stream: MediaStream) => void,
		onConnectionStateChange?: (state: RTCPeerConnectionState) => void,
	): Promise<RTCPeerConnection> {
		this.currentRoomId = roomId;
		this.remoteStream = new MediaStream();

		const pc = new RTCPeerConnection(ICE_SERVERS);
		this.peerConnection = pc;
		const myPeerId = this.peerId;

		if (this.localStream) {
			this.localStream.getTracks().forEach((track) => {
				pc.addTrack(track, this.localStream!);
			});
		}

		pc.ontrack = (event) => {
			if (!this.remoteStream) return;
			event.streams[0]?.getTracks().forEach((track) => {
				this.remoteStream?.addTrack(track);
			});
			onRemoteStream(this.remoteStream);
		};

		pc.onicecandidate = (event) => {
			if (event.candidate) {
				WSServices.sendData("teleconsultation:ice-candidate", {
					roomId,
					peerId: myPeerId,
					candidate: event.candidate.toJSON(),
				});
			}
		};

		pc.onconnectionstatechange = () => {
			onConnectionStateChange?.(pc.connectionState);
		};

		// ── Signaling listeners ───────────────────────────────────
		// IMPORTANTE: Filtramos por roomId E ignoramos mensagens do próprio peerId

		WSServices.onReceive<{ roomId: string; peerId: string; offer: RTCSessionDescriptionInit }>("teleconsultation:offer", async (data) => {
			if (data.roomId !== roomId || data.peerId === myPeerId) return;
			if (pc.signalingState === "closed") return;
			console.log(`[TC] Recebeu offer de ${data.peerId}`);
			await pc.setRemoteDescription(new RTCSessionDescription(data.offer));
			const answer = await pc.createAnswer();
			await pc.setLocalDescription(answer);
			WSServices.sendData("teleconsultation:answer", { roomId, peerId: myPeerId, answer });
		});

		WSServices.onReceive<{ roomId: string; peerId: string; answer: RTCSessionDescriptionInit }>("teleconsultation:answer", async (data) => {
			if (data.roomId !== roomId || data.peerId === myPeerId) return;
			if (pc.signalingState === "closed") return;
			console.log(`[TC] Recebeu answer de ${data.peerId}`);
			await pc.setRemoteDescription(new RTCSessionDescription(data.answer));
		});

		WSServices.onReceive<{ roomId: string; peerId: string; candidate: RTCIceCandidateInit }>("teleconsultation:ice-candidate", async (data) => {
			if (data.roomId !== roomId || data.peerId === myPeerId) return;
			if (pc.signalingState === "closed") return;
			try {
				await pc.addIceCandidate(new RTCIceCandidate(data.candidate));
			} catch (err) {
				console.warn("ICE candidate error:", err);
			}
		});

		WSServices.onReceive<{ roomId: string; peerId: string }>("teleconsultation:peer-joined", async (data) => {
			if (data.roomId !== roomId || data.peerId === myPeerId) return;
			if (pc.signalingState === "closed") return;
			console.log(`[TC] Peer ${data.peerId} entrou — criando offer`);
			const offer = await pc.createOffer();
			await pc.setLocalDescription(offer);
			WSServices.sendData("teleconsultation:offer", { roomId, peerId: myPeerId, offer });
		});

		WSServices.onReceive<{ roomId: string; peerId: string }>("teleconsultation:ended", (data) => {
			if (data.roomId !== roomId || data.peerId === myPeerId) return;
			console.log(`[TC] Peer ${data.peerId} saiu — encerrando`);
			this.stopTranscription();
			this.peerConnection?.close();
			this.peerConnection = null;
			this.localStream?.getTracks().forEach((t) => t.stop());
			this.localStream = null;
			this.remoteStream = null;
			this.currentRoomId = null;
		});

		// Chat — ignora próprias mensagens
		WSServices.onReceive<{ roomId: string; peerId: string; message: string; sender: string; time: string }>("teleconsultation:chat", (data) => {
			if (data.roomId !== roomId || data.peerId === myPeerId) return;
			this._chatCallbacks.forEach((cb) => cb(data));
		});

		// Envia join com peerId
		WSServices.sendData("teleconsultation:join", { roomId, peerId: myPeerId });
		console.log(`[TC] Join sala ${roomId} com peerId ${myPeerId}`);

		return pc;
	}

	// ── Chat ──────────────────────────────────────────────────────

	static sendChatMessage(roomId: string, message: string, senderName: string) {
		const time = new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
		WSServices.sendData("teleconsultation:chat", { roomId, peerId: this.peerId, message, sender: senderName, time });
	}

	static onChatMessage(callback: (data: { roomId: string; message: string; sender: string; time: string }) => void) {
		this._chatCallbacks.push(callback);
		return {
			stop: () => {
				this._chatCallbacks = this._chatCallbacks.filter((cb) => cb !== callback);
			},
		};
	}

	// ── Transcrição (Web Speech API) ──────────────────────────────

	static startTranscription(speakerRole: "doctor" | "patient", onUpdate?: (entries: TranscriptionEntry[]) => void) {
		this.transcription = [];
		this.onTranscriptionUpdate = onUpdate ?? null;

		const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
		if (!SpeechRecognition) {
			console.warn("[TC Transcription] Web Speech API não suportada neste navegador");
			// Adiciona uma entrada informativa
			this.transcription.push({
				timestamp: Date.now(),
				speaker: speakerRole,
				text: "⚠️ Transcrição não suportada neste navegador. Use o Google Chrome.",
			});
			this.onTranscriptionUpdate?.(this.getTranscription());
			return;
		}

		const recognition = new SpeechRecognition();
		recognition.continuous = true;
		recognition.interimResults = true; // Resultados parciais para feedback imediato
		recognition.lang = "pt-BR";
		recognition.maxAlternatives = 1;

		console.log("[TC Transcription] Iniciando reconhecimento de voz...");

		recognition.onstart = () => {
			console.log("[TC Transcription] ✅ Reconhecimento de voz ativo");
		};

		recognition.onresult = (event: any) => {
			for (let i = event.resultIndex; i < event.results.length; i++) {
				if (event.results[i].isFinal) {
					const text = event.results[i][0].transcript.trim();
					if (text) {
						console.log(`[TC Transcription] Texto final: "${text}"`);
						const entry: TranscriptionEntry = {
							timestamp: Date.now(),
							speaker: speakerRole,
							text,
						};
						this.transcription.push(entry);
						this.onTranscriptionUpdate?.(this.getTranscription());

						if (this.currentRoomId) {
							WSServices.sendData("teleconsultation:transcription", {
								roomId: this.currentRoomId,
								peerId: this.peerId,
								entry,
							});
						}
					}
				}
			}
		};

		recognition.onerror = (event: any) => {
			console.warn("[TC Transcription] Erro:", event.error, event.message);
			if (event.error === "not-allowed") {
				this.transcription.push({
					timestamp: Date.now(),
					speaker: speakerRole,
					text: "⚠️ Permissão de microfone negada para transcrição.",
				});
				this.onTranscriptionUpdate?.(this.getTranscription());
				// Não tenta reiniciar
				this.recognition = null;
				return;
			}
			// Para outros erros (no-speech, network), deixa o onend reiniciar
		};

		recognition.onend = () => {
			console.log("[TC Transcription] Reconhecimento parou, tentando reiniciar...");
			if (this.recognition && this.currentRoomId) {
				setTimeout(() => {
					if (this.recognition && this.currentRoomId) {
						try {
							recognition.start();
							console.log("[TC Transcription] Reiniciado com sucesso");
						} catch (e) {
							console.warn("[TC Transcription] Falha ao reiniciar:", e);
						}
					}
				}, 300);
			}
		};

		this.recognition = recognition;
		try {
			recognition.start();
			console.log("[TC Transcription] start() chamado");
		} catch (e) {
			console.error("[TC Transcription] Erro ao iniciar:", e);
			this.transcription.push({
				timestamp: Date.now(),
				speaker: speakerRole,
				text: "⚠️ Não foi possível iniciar a transcrição automática.",
			});
			this.onTranscriptionUpdate?.(this.getTranscription());
		}
	}

	static stopTranscription() {
		if (this.recognition) {
			const r = this.recognition;
			this.recognition = null;
			try { r.stop(); } catch { /* ignore */ }
		}
	}

	static getTranscription(): TranscriptionEntry[] {
		return [...this.transcription];
	}

	static clearTranscription() {
		this.transcription = [];
	}

	static onRemoteTranscription(callback: (entry: TranscriptionEntry) => void) {
		return WSServices.onReceive<{ roomId: string; peerId: string; entry: TranscriptionEntry }>("teleconsultation:transcription", (data) => {
			if (data.roomId !== this.currentRoomId || data.peerId === this.peerId) return;
			this.transcription.push(data.entry);
			this.onTranscriptionUpdate?.(this.getTranscription());
			callback(data.entry);
		});
	}

	// ── Controls ──────────────────────────────────────────────────

	static toggleAudio(enabled?: boolean): boolean {
		if (!this.localStream) return false;
		const audioTrack = this.localStream.getAudioTracks()[0];
		if (!audioTrack) return false;
		audioTrack.enabled = enabled ?? !audioTrack.enabled;
		return audioTrack.enabled;
	}

	static toggleVideo(enabled?: boolean): boolean {
		if (!this.localStream) return false;
		const videoTrack = this.localStream.getVideoTracks()[0];
		if (!videoTrack) return false;
		videoTrack.enabled = enabled ?? !videoTrack.enabled;
		return videoTrack.enabled;
	}

	static isAudioEnabled(): boolean {
		return this.localStream?.getAudioTracks()[0]?.enabled ?? false;
	}

	static isVideoEnabled(): boolean {
		return this.localStream?.getVideoTracks()[0]?.enabled ?? false;
	}

	// ── Cleanup ───────────────────────────────────────────────────

	static hangUp(): void {
		this.stopTranscription();

		if (this.currentRoomId) {
			WSServices.sendData("teleconsultation:leave", { roomId: this.currentRoomId, peerId: this.peerId });
		}

		this.peerConnection?.close();
		this.peerConnection = null;
		this.localStream?.getTracks().forEach((t) => t.stop());
		this.localStream = null;
		this.remoteStream = null;
		this.currentRoomId = null;
		this._chatCallbacks = [];
	}

	static getCurrentRoomId(): string | null {
		return this.currentRoomId;
	}

	static getPeerId(): string {
		return this.peerId;
	}
}
